import { useMutation, useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { store } from '../..';
import { queryClient } from '../../..';
import { leaves } from '../../../api/hr/leave-management';
import { setAlert, setIsLoading } from '../../components/components-slice';

export function useGetLeaveSummary() {
  return useQuery(['leave-summary'], () => {
    return leaves.getLeaveSummary();
  });
}

export function useGetLeaves(limit, page, statusFilter, search, source, source_id) {
  return useQuery(
    ['leaves', { limit, page, statusFilter, search, source, source_id }],
    () => {
      store.dispatch(setIsLoading(true));
      return leaves.getLeaves({
        limit,
        page,
        statusFilter,
        search,
        source, 
        source_id
      });
    },
    {
      enabled: source_id !== null,
      select: (data) => {
        if (data?.data) {
          data?.data?.forEach((leave) => {
            if (source == 'team' && leave.team_status == 0) {
              switch (leave.final_status) {
                case 0:
                  leave.computedActions = [0, 1, 2];
                  break;
                case 1:
                  leave.computedActions = [0];
                  break;
                case 2:
                  leave.computedActions = [0];
                  break;
                default:
                  break;
              }
            } else if (source == 'department' && leave.department_status == 0) {
              switch (leave.final_status) {
                case 0:
                  leave.computedActions = [0, 1, 2];
                  break;
                case 1:
                  leave.computedActions = [0];
                  break;
                case 2:
                  leave.computedActions = [0];
                  break;
                default:
                  break;
              }
            } else if (source == 'branch' && leave.branch_status == 0) {
              switch (leave.final_status) {
                case 0:
                  leave.computedActions = [0, 1, 2];
                  break;
                case 1:
                  leave.computedActions = [0];
                  break;
                case 2:
                  leave.computedActions = [0];
                  break;
                default:
                  break;
              }
            } else {
              leave.computedActions = [0];
            }

            
            leave.date_created = leave.created.split('T')[0];
            leave.status = leave.final_status;
            leave.period = `${moment(leave.start_date).format('MMMM D, YYYY')} to ${moment(leave.end_date).format('MMMM D, YYYY')}`
            leave.employeeName = `${leave.first_name} ${leave.last_name}`;
          });
        }

        return data;
      },

      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
      // keepPreviousData: true
    },
  );
}

export function useGetLeaveTypes(limit, page, search) {
  return useQuery(
    ['leave-types', { limit, page, search }],
    () => {
      store.dispatch(setIsLoading(true));
      return leaves.getLeaveTypes({
        limit,
        page,
        search,
      });
    },
    {
      select: (data) => {
        data.data.forEach((leave_type) => {
          leave_type.computedActions = [0, 1, 2];
        });
        return data;
      },

      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
      // keepPreviousData: true
    },
  );
}

export function useGetApplicationLeaveTypes(limit, page, search) {
  return useQuery(
    ['application-leave-types', { limit, page, search }],
    () => {
      store.dispatch(setIsLoading(true));
      return leaves.getLeaveTypes({
        limit,
        page,
        search,
      });
    },
    {
      select: (data) => {
        let newData = [];
        newData.push({ value: '', label: 'No Leave Type Selected' });
        data.forEach((item) => {
          newData.push({ value: item.name, label: item.name });
        });
        return newData;
      },

      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
      // keepPreviousData: true
    },
  );
}

export function useAddLeave() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true));
      return leaves.addLeave(payload);
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('leaves');
        store.dispatch(setAlert(true, 'success', response.message));
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}

export function useEditLeave() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true));
      return leaves.updateLeave(payload);
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('leaves');
        store.dispatch(setAlert(true, 'success', response.message));
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}

export function useDeleteLeave() {
  return useMutation(
    (payload) => {
      return leaves.deleteLeave(payload);
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('leaves');
        store.dispatch(setAlert(true, 'success', response.message));
      },
    },
  );
}

export function useGetLeave(id) {
  return useQuery(
    ['leave', { id }],
    () => {
      return leaves.getLeave({ id });
    },
    {
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}

export function useApproveLeave() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true));
      return leaves.approveLeave(payload);
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('leaves');
        store.dispatch(setAlert(true, 'success', response.message));
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}

export function useGetLeavesByEmployee(
  employee_id,
  limit,
  page,
  statusFilter,
  search,
  start_date,
  end_date,
) {
  return useQuery(
    [
      'single-employee-leaves',
      { employee_id, limit, page,statusFilter, search, start_date, end_date },
    ],
    () => {
      store.dispatch(setIsLoading(true));
      return leaves.getLeaveByEmployee({
        employee_id,
        limit,
        page,
        statusFilter,
        search,
        start_date,
        end_date,
      });
    },
    {
      enabled: employee_id !== null ? true : false,
      select(data) {
        data.data.forEach((leave) => {
          leave.employeeName = leave.first_name + ' ' + leave.last_name
          leave.date_created = leave.created.split('T')[0];
          leave.status = leave.final_status;
          leave.period = `${leave.start_date} to ${leave.end_date}`;
          leave.employeeName = `${leave.first_name} ${leave.last_name}`;
          leave.computedActions = [0, 1];
        });

        return data;
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
      // keepPreviousData: true
    },
  );
}

export function useDeclineLeave() {
  return useMutation(
    (payload) => {
      store.dispatch(setIsLoading(true));
      return leaves.declineLeave(payload);
    },
    {
      onSuccess: (response, variables, context) => {
        queryClient.invalidateQueries('leaves');
        store.dispatch(setAlert(true, 'success', response.message));
      },
      onSettled: (data, error, variables, context) => {
        store.dispatch(setIsLoading(false));
      },
    },
  );
}
